import { Component, EventEmitter, Inject, Injector, Input, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';

import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, AuthService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, SITEMAP } from 'pbc.angular';

import { IPostRechnungsVorlageRequest, IPostRechnungsVorlageResponse } from 'fa-kt.types';
import { PostRechnungsVorlageCommandService } from '../service';

@Component({
  selector: 'fa-kt-post-rechnungs-vorlage',
  templateUrl: './post-rechnungs-vorlage.component.html',
  styleUrls: ['./post-rechnungs-vorlage.component.css'],
})
export class PostRechnungsVorlageCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IPostRechnungsVorlageRequest) {
    this.patch(value);
  }
  @Output() valueChanged: EventEmitter<IPostRechnungsVorlageRequest> = new EventEmitter<IPostRechnungsVorlageRequest>();
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostRechnungsVorlageResponse> = new EventEmitter<IPostRechnungsVorlageResponse>();

  get modalRef() {
    return this.injector.get(NzModalRef);
  }

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private injector: Injector,
    private viewContainerRef: ViewContainerRef,
    private modal: NzModalService,
    private message: NzMessageService,
    private actions: ActionService,
    public auth: AuthService,
    public postRechnungsVorlage: PostRechnungsVorlageCommandService /* << more    */ /*    more >> */,
  ) {
    this.command = sitemap.FINANZEN.Commands.POST_RECHNUNGS_VORLAGE;
    this.form = this.fb.group({
      rechnungsVorlage: this.fb.group({
        id: [null, []],
        name: [null, [Validators.required]],
        kommentar: [null, []],
        datei: [null, [Validators.required]],
      }),
    });
    /* << constructor    */
    /*    constructor >> */
    this.patch();
  }

  ngOnInit() {
    this.postRechnungsVorlage.prepare().catch((e) => console.error(e));
    this.subscriptions.push(
      this.finished.subscribe((result) => {
        if (this.modalRef) {
          this.modalRef.destroy(result);
        }
      }),
      this.valid.subscribe((valid) => {
        if (valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.form.valueChanges.subscribe((value: IPostRechnungsVorlageRequest) => {
        this.valueChanged.emit(value);
        this.valid.emit(this.form.valid);
      }),
      this.rechnungsVorlageId?.valueChanges.subscribe(async (id) => {
        /* << on-rechnungs-vorlage-id-change    */
        /*    on-rechnungs-vorlage-id-change >> */
      }) as Subscription,
      this.rechnungsVorlagename?.valueChanges.subscribe(async (name) => {
        /* << on-rechnungs-vorlage-name-change    */
        /*    on-rechnungs-vorlage-name-change >> */
      }) as Subscription,
      this.rechnungsVorlageKommentar?.valueChanges.subscribe(async (kommentar) => {
        /* << on-rechnungs-vorlage-kommentar-change    */
        /*    on-rechnungs-vorlage-kommentar-change >> */
      }) as Subscription,
      this.rechnungsVorlageDatei?.valueChanges.subscribe(async (datei) => {
        /* << on-rechnungs-vorlage-datei-change    */
        /*    on-rechnungs-vorlage-datei-change >> */
      }) as Subscription,
      /* << subscibe    */
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    /* << end    */
    /*    end >> */
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IPostRechnungsVorlageRequest) {
    if (value) {
      value = cleanObject(value);
      if (value.rechnungsVorlage) {
        value.rechnungsVorlage = cleanObject(value.rechnungsVorlage);
      }
      this.form.patchValue(value);
    }
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload.rechnungsVorlage) {
        payload.rechnungsVorlage = cleanObject(payload.rechnungsVorlage);
      }
      const response: IPostRechnungsVorlageResponse = await this.postRechnungsVorlage.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls[validation.property].get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get rechnungsVorlageId() {
    return this.form.controls.rechnungsVorlage.get('id');
  }
  get rechnungsVorlagename() {
    return this.form.controls.rechnungsVorlage.get('name');
  }
  get rechnungsVorlageKommentar() {
    return this.form.controls.rechnungsVorlage.get('kommentar');
  }
  get rechnungsVorlageDatei() {
    return this.form.controls.rechnungsVorlage.get('datei');
  }
}
